const MENU_ITEM_PREFIX = 'menu-item-';
const CLOSE_MS = 120;

class HeaderMenu extends HTMLElement {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {HTMLElement | null} */
  #header = null;

  /** @type {string | null} */
  #activeEditorBlockId = null;

  /** @type {string | null} */
  #activeClickHandle = null;

  /** @param {HTMLElement} li */
  #handleForItem(li) {
    const ds = li.dataset?.megaHandle;
    if (ds) return ds;
    return [...li.classList].find((c) => c.startsWith(MENU_ITEM_PREFIX))?.slice(MENU_ITEM_PREFIX.length) ?? null;
  }
  /** @type {HTMLElement | null} */
  #body = null;

  /** @param {HTMLElement} el @param {'content' | 'products' | 'promo'} variant */
  #playMegaEnterAnimation(el, variant) {
    const className =
      variant === 'promo'
        ? 'mega-panel--enter-promo'
        : variant === 'products'
          ? 'mega-panel--enter-products'
          : 'mega-panel--enter-content';

    el.classList.remove('mega-panel--enter-content', 'mega-panel--enter-products', 'mega-panel--enter-promo');
    // Force reflow so repeated navigation re-triggers the animation.
    void el.offsetWidth;
    el.classList.add(className);
    el.addEventListener(
      'animationend',
      () => {
        el.classList.remove(className);
      },
      { once: true }
    );
  }

  /**
   * @param {HTMLElement} panel
   * @param {string} l1Key
   * @param {boolean} [animate]
   */
  #animateMegaLevel1(panel, l1Key, animate = true) {
    if (!animate) return;

    const subpanel = panel.querySelector(`[data-mega-subpanel="${CSS.escape(l1Key)}"]`);
    if (subpanel instanceof HTMLElement) {
      this.#playMegaEnterAnimation(subpanel, 'content');
    }

    const l2Panel = subpanel?.querySelector('[data-mega-l2-panel]');
    const activeL2 = l2Panel instanceof HTMLElement ? l2Panel.getAttribute('data-active-l2') : null;
    if (activeL2) this.#animateMegaLevel2(panel, l1Key, activeL2, animate);
  }

  /**
   * @param {HTMLElement} panel
   * @param {string} l1Key
   * @param {string} l2Key
   */
  #syncMegaPromo(panel, l1Key, l2Key) {
    panel.querySelectorAll('[data-mega-promo-l1]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const match = el.dataset.megaPromoL1 === l1Key && el.dataset.megaPromoL2 === l2Key;
      el.toggleAttribute('hidden', !match);
    });
  }

  /**
   * @param {HTMLElement} panel
   * @param {string} l1Key
   * @param {string} l2Key
   * @param {boolean} [animate]
   */
  #animateMegaLevel2(panel, l1Key, l2Key, animate = true) {
    this.#syncMegaPromo(panel, l1Key, l2Key);
    if (!animate) return;

    const subpanel = panel.querySelector(`[data-mega-subpanel="${CSS.escape(l1Key)}"]`);
    const l3Panel = subpanel?.querySelector(`[data-mega-l3-subpanel="${CSS.escape(l2Key)}"]`);
    if (l3Panel instanceof HTMLElement) {
      this.#playMegaEnterAnimation(l3Panel, 'products');
    }

    const promo = panel.querySelector(
      `[data-mega-promo-l1="${CSS.escape(l1Key)}"][data-mega-promo-l2="${CSS.escape(l2Key)}"]`
    );
    if (promo instanceof HTMLElement && !promo.hasAttribute('hidden')) {
      this.#playMegaEnterAnimation(promo, 'promo');
    }
  }

  /**
   * @param {AbortSignal} signal
   * @param {'hover' | 'click'} openMode
   */
  #bindMegaPanelCategoryInteractions(signal, openMode) {
    if (!this.#header) return;

    for (const container of this.#header.querySelectorAll('.mega-menu-container[data-mega-layout="categories"]')) {
      if (!(container instanceof HTMLElement)) continue;

      const panel = container.querySelector('[data-mega-panel]');
      if (!(panel instanceof HTMLElement)) continue;

      /** @param {string} l1Key @param {string} l2Key @param {boolean} [animate] */
      const syncPromo = (l1Key, l2Key, animate = true) => {
        this.#syncMegaPromo(panel, l1Key, l2Key);
        if (!animate) return;

        const promo = panel.querySelector(
          `[data-mega-promo-l1="${CSS.escape(l1Key)}"][data-mega-promo-l2="${CSS.escape(l2Key)}"]`
        );
        if (promo instanceof HTMLElement && !promo.hasAttribute('hidden')) {
          this.#playMegaEnterAnimation(promo, 'promo');
        }
      };

      /** @param {string} key @param {boolean} [animate] */
      const setActive = (key, animate = true) => {
        panel.setAttribute('data-active-key', key);

        panel.querySelectorAll('[data-mega-key]').forEach((el) => {
          if (!(el instanceof HTMLElement)) return;
          el.classList.toggle('is-active', el.dataset.megaKey === key);
        });

        panel.querySelectorAll('[data-mega-subpanel]').forEach((el) => {
          if (!(el instanceof HTMLElement)) return;
          el.toggleAttribute('hidden', el.dataset.megaSubpanel !== key);
        });

        panel.querySelectorAll('[data-mega-products]').forEach((el) => {
          if (!(el instanceof HTMLElement)) return;
          el.toggleAttribute('hidden', el.dataset.megaProducts !== key);
        });

        this.#animateMegaLevel1(panel, key, animate);
      };

      /** @param {HTMLElement} l2Panel */
      const bindLevel2 = (l2Panel) => {
        /** @param {string} l2Key @param {boolean} [animate] */
        const setL2 = (l2Key, animate = true) => {
          l2Panel.setAttribute('data-active-l2', l2Key);
          l2Panel.querySelectorAll('[data-mega-l2-key]').forEach((el) => {
            if (!(el instanceof HTMLElement)) return;
            el.classList.toggle('is-active', el.dataset.megaL2Key === l2Key);
          });
          l2Panel.querySelectorAll('[data-mega-l3-subpanel]').forEach((el) => {
            if (!(el instanceof HTMLElement)) return;
            el.toggleAttribute('hidden', el.dataset.megaL3Subpanel !== l2Key);
          });

          const subpanel = l2Panel.closest('[data-mega-subpanel]');
          const l1Key = subpanel instanceof HTMLElement ? subpanel.dataset.megaSubpanel : null;
          if (l1Key) {
            if (animate) {
              this.#animateMegaLevel2(panel, l1Key, l2Key, animate);
            } else {
              syncPromo(l1Key, l2Key, false);
            }
          }
        };

        const defaultL2 =
          l2Panel.getAttribute('data-active-l2') ??
          (l2Panel.querySelector('[data-mega-l2-key]') instanceof HTMLElement
            ? /** @type {HTMLElement} */ (l2Panel.querySelector('[data-mega-l2-key]'))?.dataset.megaL2Key
            : null);
        if (defaultL2) setL2(defaultL2, false);

        for (const item of l2Panel.querySelectorAll('[data-mega-l2-key]')) {
          if (!(item instanceof HTMLElement)) continue;

          const applyL2 = () => {
            const l2Key = item.dataset.megaL2Key;
            if (!l2Key) return;
            setL2(l2Key);
          };

          if (openMode === 'hover') {
            item.addEventListener('pointerenter', applyL2, { signal });
          } else {
            const link = item.querySelector('a.mega-menu__level2-link');
            if (!(link instanceof HTMLAnchorElement)) continue;
            // Single L2 item: navigate to the page instead of switching panels.
            if (link.hasAttribute('data-mega-direct')) continue;
            link.addEventListener(
              'click',
              (e) => {
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                applyL2();
              },
              { signal }
            );
          }
        }
      };

      // Ensure default is applied even if DOM re-hydrates.
      const defaultKey =
        panel.getAttribute('data-active-key') ??
        (panel.querySelector('[data-mega-key]') instanceof HTMLElement
          ? /** @type {HTMLElement} */ (panel.querySelector('[data-mega-key]'))?.dataset.megaKey
          : null);
      if (defaultKey) setActive(defaultKey, false);

      // Bind level-2 hover switching for each level-1 subpanel.
      for (const subpanel of panel.querySelectorAll('[data-mega-subpanel]')) {
        if (!(subpanel instanceof HTMLElement)) continue;
        const l2Panel = subpanel.querySelector('[data-mega-l2-panel]');
        if (l2Panel instanceof HTMLElement) bindLevel2(l2Panel);
      }

      for (const item of panel.querySelectorAll('[data-mega-key]')) {
        if (!(item instanceof HTMLElement)) continue;

        const applyL1 = () => {
          const key = item.dataset.megaKey;
          if (!key) return;
          setActive(key);
        };

        if (openMode === 'hover') {
          item.addEventListener('pointerenter', applyL1, { signal });
        } else {
          const link = item.querySelector('a.mega-menu__level1-link');
          if (!(link instanceof HTMLAnchorElement)) continue;
          // Single L1 item: navigate to the page instead of switching panels.
          if (link.hasAttribute('data-mega-direct')) continue;
          link.addEventListener(
            'click',
            (e) => {
              if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              e.preventDefault();
              applyL1();
            },
            { signal }
          );
        }
      }
    }
  }

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    this.#body = document.body ?? null;
    const { signal } = this.#abort;

    this.#header = this.closest('#header-component');
    if (!this.#header) return;

    const openMode = this.dataset.megaOpenMode === 'click' ? 'click' : 'hover';

    this.#bindMegaPanelCategoryInteractions(signal, openMode);

    if (window.Shopify?.designMode) {
      document.addEventListener(
        'shopify:block:select',
        /** @param {CustomEvent} e */ (e) => {
          const blockId = e?.detail?.blockId;
          if (!blockId || !this.#header) return;

          const panel = this.#header.querySelector(`.mega-menu-container[data-block-id="${CSS.escape(blockId)}"]`);
          if (!(panel instanceof HTMLElement)) return;

          clearTimeout(this.#closeTimer);
          this.#activeEditorBlockId = blockId;
          this.#activeClickHandle = null;
          this.#clearMega();
          panel.setAttribute('data-mega-active', '');
        },
        { signal }
      );

      document.addEventListener(
        'shopify:block:deselect',
        /** @param {CustomEvent} e */ (e) => {
          const blockId = e?.detail?.blockId;
          if (!blockId || blockId !== this.#activeEditorBlockId) return;
          this.#activeEditorBlockId = null;
          this.#clearMega();
        },
        { signal }
      );
    }

    if (openMode === 'click') {
      for (const li of this.querySelectorAll('li.menu-list__list-item')) {
        if (!(li instanceof HTMLElement)) continue;
        const handle = this.#handleForItem(li);
        if (!handle) continue;

        const link = li.querySelector('a.menu-list__link');
        if (!(link instanceof HTMLAnchorElement)) continue;

        if (this.#mega(handle)) {
          link.setAttribute('aria-haspopup', 'true');
          link.setAttribute('aria-expanded', 'false');
        }

        link.addEventListener(
          'click',
          (e) => {
            if (this.#activeEditorBlockId) return;
            if (!this.#mega(handle)) return;
            e.preventDefault();
            e.stopPropagation();

            if (this.#activeClickHandle === handle) {
              this.#activeClickHandle = null;
              this.#clearMega();
              return;
            }

            this.#activeClickHandle = handle;
            this.#open(handle);
          },
          { signal }
        );
      }

      document.addEventListener(
        'pointerdown',
        (e) => {
          if (this.#activeEditorBlockId) return;
          if (!this.#activeClickHandle) return;

          const activePanel = this.#mega(this.#activeClickHandle);
          const target = e.target;
          if (target instanceof Node) {
            if (this.contains(target)) return;
            if (activePanel && activePanel.contains(target)) return;
          }

          this.#activeClickHandle = null;
          this.#clearMega();
        },
        { signal }
      );

      document.addEventListener(
        'keydown',
        (e) => {
          if (this.#activeEditorBlockId) return;
          if (e.key !== 'Escape') return;
          if (!this.#activeClickHandle) return;
          this.#activeClickHandle = null;
          this.#clearMega();
        },
        { signal }
      );
    } else {
      for (const li of this.querySelectorAll('li.menu-list__list-item')) {
        if (!(li instanceof HTMLElement)) continue;
        const handle = this.#handleForItem(li);
        if (!handle) continue;

        li.addEventListener('pointerenter', () => this.#open(handle), { signal });
        li.addEventListener(
          'pointerleave',
          /** @param {PointerEvent} e */ (e) => {
            if (this.#activeEditorBlockId) return;
            const panel = this.#mega(handle);
            const next = e.relatedTarget;
            if (panel && next instanceof Node && panel.contains(next)) return;
            this.#closeSoon();
          },
          { signal }
        );
      }

      for (const panel of this.#header.querySelectorAll('.mega-menu-container[class*="menu-for-"]')) {
        panel.addEventListener('pointerenter', () => clearTimeout(this.#closeTimer), { signal });
        panel.addEventListener(
          'pointerleave',
          /** @param {PointerEvent} e */ (e) => {
            if (this.#activeEditorBlockId) return;
            const next = e.relatedTarget;
            if (next instanceof Node && this.contains(next)) return;
            this.#closeSoon();
          },
          { signal }
        );
      }
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = undefined;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
    this.#activeClickHandle = null;
    this.#clearMega();
    this.#header = null;
  }

  /** @param {string} handle */
  #mega(handle) {
    return this.#header?.querySelector(`.mega-menu-container.menu-for-${CSS.escape(handle)}`) ?? null;
  }

  /** @param {string} handle */
  #link(handle) {
    return (
      this.querySelector(`.menu-list__list-item.${MENU_ITEM_PREFIX}${CSS.escape(handle)} a.menu-list__link`) ??
      this.querySelector(`.menu-list__list-item.menu-item-${CSS.escape(handle)} a.menu-list__link`) ??
      null
    );
  }

  /** @param {string} handle @param {boolean} expanded */
  #setExpanded(handle, expanded) {
    const link = this.#link(handle);
    if (!(link instanceof HTMLElement)) return;
    link.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  #clearMega() {
    if (this.#activeClickHandle) this.#setExpanded(this.#activeClickHandle, false);
    this.#header?.querySelectorAll('.mega-menu-container[data-mega-active]').forEach((el) => el.removeAttribute('data-mega-active'));
    this.querySelectorAll('.menu-list__link[aria-expanded="true"]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
    this.#body?.removeAttribute('data-mega-open');
  }

  /** @param {string} handle */
  #open(handle) {
    clearTimeout(this.#closeTimer);
    this.#clearMega();
    const container = this.#mega(handle);
    container?.setAttribute('data-mega-active', '');
    this.#setExpanded(handle, true);
    this.#body?.setAttribute('data-mega-open', '');

    const panel = container?.querySelector('[data-mega-panel]');
    const activeKey = panel instanceof HTMLElement ? panel.getAttribute('data-active-key') : null;
    if (panel instanceof HTMLElement && activeKey) {
      const subpanel = panel.querySelector(`[data-mega-subpanel="${CSS.escape(activeKey)}"]`);
      const l2Panel = subpanel?.querySelector('[data-mega-l2-panel]');
      const activeL2 = l2Panel instanceof HTMLElement ? l2Panel.getAttribute('data-active-l2') : null;
      if (activeL2) {
        this.#syncMegaPromo(panel, activeKey, activeL2);
      }
    }
  }

  #closeSoon() {
    if (this.#activeEditorBlockId) return;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = undefined;
      this.#clearMega();
      this.#body?.removeAttribute('data-mega-open');
    }, CLOSE_MS);
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}
